export const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, value));

export const sum = (values: number[]): number =>
  values.reduce((accumulator, value) => accumulator + value, 0);

const npv = (rate: number, cashFlows: number[]): number =>
  cashFlows.reduce(
    (accumulator, cashFlow, index) =>
      accumulator + cashFlow / Math.pow(1 + rate, index),
    0
  );

export const annualizedIrr = (cashFlows: number[]): number => {
  if (cashFlows.length < 2) return Number.NaN;

  const hasPositive = cashFlows.some((value) => value > 0);
  const hasNegative = cashFlows.some((value) => value < 0);
  if (!hasPositive || !hasNegative) return Number.NaN;

  // Robust bisection search. Newton can explode for common property cashflow shapes.
  let low = -0.99;
  let high = 1;
  let lowValue = npv(low, cashFlows);
  let highValue = npv(high, cashFlows);

  // Expand upper bound until we bracket a root, within a sensible cap.
  for (let i = 0; i < 60 && Math.sign(lowValue) === Math.sign(highValue); i += 1) {
    high *= 1.5;
    if (high > 50) break;
    highValue = npv(high, cashFlows);
  }

  if (Math.sign(lowValue) === Math.sign(highValue)) {
    return Number.NaN;
  }

  let mid = 0;
  for (let i = 0; i < 200; i += 1) {
    mid = (low + high) / 2;
    const midValue = npv(mid, cashFlows);

    if (!Number.isFinite(midValue)) return Number.NaN;
    if (Math.abs(midValue) < 1e-6) return mid;

    if (Math.sign(midValue) === Math.sign(lowValue)) {
      low = mid;
      lowValue = midValue;
    } else {
      high = mid;
      highValue = midValue;
    }

    if (Math.abs(high - low) < 1e-7) return mid;
  }

  return mid;
};

export const formatPercent = (value: number): string =>
  `${(value * 100).toFixed(2)}%`;
export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0
  }).format(value);
